import React, { useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { EuiIcon } from '@elastic/eui';
import { BoxedResponsiveStyled } from '../../../../../style/components/common.styled';
import utilsService from '../../../../../servises/utils.service';
import usageStore from '../../../../../stores/usage.store';
import UsageChart from './UsageChart';

const UsageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 1px 10px;
  margin-top: 0;
  grid-gap: 11px;

  @media (max-width: 1025px) {
    grid-template-columns: 1fr;
  }
`;

const UsageSection = styled(BoxedResponsiveStyled)`
  margin: 0 auto 10px auto;
  padding: 18px;

  .usage-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
  }

  .usage-header h3 {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
  }

  select {
    min-width: 110px;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 22px;
  }

  .summary-card {
    padding: 13px;
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.025);
  }

  .summary-card span {
    display: block;
    margin-bottom: 6px;
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
  }

  .summary-card strong {
    font-size: 17px;
  }

  .usage-message {
    padding: 28px 10px;
    color: rgba(255, 255, 255, 0.55);
    text-align: center;
  }

  .usage-note {
    margin: 16px 0 0;
    color: rgba(255, 255, 255, 0.42);
    font-size: 11px;
  }

  @media (max-width: 800px) {
    .summary-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 520px) {
    padding: 14px;

    .usage-header {
      align-items: flex-start;
      flex-direction: column;
    }

    .summary-grid {
      grid-template-columns: 1fr;
    }

    select {
      width: 100%;
    }
  }
`;

const RANGE_OPTIONS = [
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: '1y', label: '1 year' }
];

const ServerUsageSection = ({ serverId, enabled }) => {
    useEffect(() => {
        if (enabled && serverId) {
            usageStore.getUsage(serverId);
        }
        return () => usageStore.clear();
    }, [serverId, enabled]);

    return useObserver(() => {
        if (!enabled) {
            return null;
        }

        const usage = usageStore.usage;
        const networkChart = buildNetworkChart(usage.points, usage.granularity);
        const sshChart = buildSSHChart(usage.points, usage.granularity);

        return (
            <UsageGrid>
                {/* Network Usage Section */}
                <UsageSection aria-labelledby="server-network-usage-title">
                    <div className="usage-header">
                        <h3 id="server-network-usage-title">
                            <EuiIcon type="globe" size="m"/> Network Usage History
                        </h3>
                        <select
                            aria-label="Network history range"
                            value={ usageStore.range }
                            disabled={ usageStore.loading }
                            onChange={ event => usageStore.setRange(event.target.value, serverId) }
                        >
                            { RANGE_OPTIONS.map(option => (
                                <option key={ option.value } value={ option.value }>
                                    { option.label }
                                </option>
                            )) }
                        </select>
                    </div>

                    { usageStore.loading && <div className="usage-message">Loading network usage...</div> }
                    { !usageStore.loading && usageStore.error && (
                        <div className="usage-message" role="alert">{ usageStore.error }</div>
                    ) }
                    { !usageStore.loading && !usageStore.error && usage.points.length === 0 && (
                        <div className="usage-message">No historical network usage has been collected yet.</div>
                    ) }
                    { !usageStore.loading && !usageStore.error && usage.points.length > 0 && (
                        <>
                            <div className="summary-grid">
                                <SummaryCard label="Network received" value={ formatBytes(usage.network.received) }/>
                                <SummaryCard label="Network transmitted" value={ formatBytes(usage.network.transmitted) }/>
                            </div>
                            <UsageChart
                                title="Network transfer"
                                data={ networkChart.data }
                                unit={ networkChart.unit }
                                description="Historical summary of total network data received and transmitted over the selected period."
                                series={ [
                                    { key: 'received', label: 'Received', color: '#06b6d4', gradientId: 'usage-network-rx' },
                                    { key: 'transmitted', label: 'Transmitted', color: '#f97316', gradientId: 'usage-network-tx' }
                                ] }
                            />
                        </>
                    ) }
                </UsageSection>

                {/* SSH Security Section */}
                <UsageSection aria-labelledby="server-ssh-usage-title">
                    <div className="usage-header">
                        <h3 id="server-ssh-usage-title">
                            <EuiIcon type="securitySignal" size="m"/> SSH Security History
                        </h3>
                        <select
                            aria-label="SSH history range"
                            value={ usageStore.range }
                            disabled={ usageStore.loading }
                            onChange={ event => usageStore.setRange(event.target.value, serverId) }
                        >
                            { RANGE_OPTIONS.map(option => (
                                <option key={ option.value } value={ option.value }>
                                    { option.label }
                                </option>
                            )) }
                        </select>
                    </div>

                    { usageStore.loading && <div className="usage-message">Loading SSH history...</div> }
                    { !usageStore.loading && usageStore.error && (
                        <div className="usage-message" role="alert">{ usageStore.error }</div>
                    ) }
                    { !usageStore.loading && !usageStore.error && usage.points.length === 0 && (
                        <div className="usage-message">No historical SSH security attempts have been collected yet.</div>
                    ) }
                    { !usageStore.loading && !usageStore.error && usage.points.length > 0 && (
                        <>
                            <div className="summary-grid">
                                <SummaryCard label="SSH successful" value={ usage.ssh.successful.toLocaleString() }/>
                                <SummaryCard label="SSH failed" value={ usage.ssh.failed.toLocaleString() }/>
                            </div>
                            <UsageChart
                                title="SSH attempts"
                                data={ sshChart }
                                unit=""
                                description="Historical summary of successful and failed SSH authentication attempts to identify potential brute-force or unauthorized access events."
                                series={ [
                                    { key: 'successful', label: 'Successful', color: '#10b981', gradientId: 'usage-ssh-success' },
                                    { key: 'failed', label: 'Failed', color: '#f43f5e', gradientId: 'usage-ssh-failed' }
                                ] }
                            />
                            { usage.limitations.map(note => (
                                <p className="usage-note" key={ note }>{ note }</p>
                            )) }
                        </>
                    ) }
                </UsageSection>
            </UsageGrid>
        );
    });
};

const SummaryCard = ({ label, value }) => (
    <div className="summary-card">
        <span>{ label }</span>
        <strong>{ value }</strong>
    </div>
);

const pointName = (timestamp, granularity) => {
    if (granularity === 'day') {
        return dayjs(timestamp).format('DD MMM');
    }
    return dayjs(timestamp).format('DD MMM HH:mm');
};

export const buildNetworkChart = (points, granularity) => {
    const maxBytes = points.reduce((max, point) => (
        Math.max(max, point.rxBytes || 0, point.txBytes || 0)
    ), 0);
    const formatted = utilsService.formatBytes(maxBytes);
    const unit = formatted.unit || 'Bytes';
    const divisor = byteDivisor(unit);

    return {
        unit,
        data: points.map(point => ({
            name: pointName(point.timestamp, granularity),
            received: roundValue((point.rxBytes || 0) / divisor),
            transmitted: roundValue((point.txBytes || 0) / divisor)
        }))
    };
};

export const buildSSHChart = (points, granularity) => points.map(point => ({
    name: pointName(point.timestamp, granularity),
    successful: point.sshSuccess || 0,
    failed: point.sshFailed || 0
}));

const byteDivisor = unit => {
    const index = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'].indexOf(unit);
    return index > 0 ? 1024 ** index : 1;
};

const roundValue = value => Number(value.toFixed(2));

const formatBytes = bytes => {
    const formatted = utilsService.formatBytes(bytes || 0);
    return `${ formatted.value } ${ formatted.unit }`;
};

export default ServerUsageSection;
